import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaEye,
  FaPrint,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const TenderListing = () => {
  const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [locations, setLocations] = useState([]);
  const [tenderData, setTenderData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    totalRows: 0,
    totalPages: 0,
  });

  const [formData, setFormData] = useState({
    nameOfWork: "",
    tenderRefNo: "",
    fromDate: "",
    clientName: "",
    location: "",
    consultantName: "",
    toDate: "",
    status: "In Process",
  });

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  const branchid = Number(branchId || 0);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const fetchWorkLocations = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/tender/work-locations`, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setLocations(res.data.data || []);
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load work locations");
      setLocations([]);
    }
  };

  function toDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${d}/${m}/${y}`;
  }

  const fetchTenders = async (override = {}) => {
    try {
      setLoading(true);

      const params = {
        page,
        limit,
        branchid,

        // form filters
        nameOfWork: formData.nameOfWork || "",
        locationId: formData.location || "",
        tenderRefNo: formData.tenderRefNo || "",
        consultantName: formData.consultantName || "",
        clientName: formData.clientName || "",
        status: formData.status || "In Process",
        fromDate: formData.fromDate ? toDDMMYYYY(formData.fromDate) : "",
        toDate: formData.toDate ? toDDMMYYYY(formData.toDate) : "",

        ...override,
      };

      const res = await axios.get(`${BASE_URL}/api/tender/list`, {
        params,
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setTenderData(res.data.data || []);
        setPagination(
          res.data.pagination || { totalRows: 0, totalPages: 0 }
        );
      } else {
        setTenderData([]);
        setPagination({ totalRows: 0, totalPages: 0 });
        toast.warning(res.data?.message || "No data");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tenders");
      setTenderData([]);
      setPagination({ totalRows: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkLocations();
    fetchTenders({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPage(1);
    fetchTenders({ page: 1 });
  };

    const handleReset = () => {
    setFormData({
      nameOfWork: "",
      tenderRefNo: "",
      fromDate: "",
      clientName: "",
      location: "",
      consultantName: "",
      toDate: "",
      status: "In Process",
    });
  };

  const handleViewAll = () => {
    setFormData({
      nameOfWork: "",
      tenderRefNo: "",
      fromDate: "",
      clientName: "",
      location: "",
      consultantName: "",
      toDate: "",
      status: "In Process",
    });

    setPage(1);

    fetchTenders({
      page: 1,
      branchid,
      nameOfWork: "",
      locationId: "",
      tenderRefNo: "",
      consultantName: "",
      clientName: "",
      fromDate: "",
      toDate: "",
      status: "In Process",
    });
  };

  const handleAddNew = () => {
    navigate("/OrderManagement/TenderDetailsEntry");
    toast.success("Add New tender clicked!");
  };

  const handleFileManager = (row) => {
    navigate(
      `/OrderManagement/ProjectFileManager?contractId=${encodeURIComponent(
        row.contractId
      )}&tenderId=${encodeURIComponent(row.tenderDetailsId)}`
    );
  };

  const handleEditAndView = (row) => {
    navigate(
      `/OrderManagement/TenderDetailsEntry?contractId=${encodeURIComponent(
        row.contractId
      )}&tenderId=${encodeURIComponent(row.tenderDetailsId)}`
    );
  };

  const handleOngoing = (row) => {
    navigate(
      `/OrderManagement/TenderSuccess?contractId=${encodeURIComponent(
        row.contractId
      )}&tenderId=${encodeURIComponent(row.tenderDetailsId)}`
    );
  };

  const handleUnsuccessful = (row) => {
    navigate(
      `/OrderManagement/TenderUnsuccess?contractId=${encodeURIComponent(
        row.contractId
      )}&tenderId=${encodeURIComponent(row.tenderDetailsId)}`
    );
  };

  const handlePrint = (row) => {
    window.open(
      `/OrderManagement/TenderDetailsReport_Print?contractId=${encodeURIComponent(
        row.contractId
      )}&tenderDetailsId=${encodeURIComponent(row.tenderDetailsId)}`,
      "_blank"
    );
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TENDER LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Tender Listing
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          <div
            className={`p-6 rounded-xl border mb-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Name of The Work
                    </label>
                    <input
                      type="text"
                      value={formData.nameOfWork}
                      onChange={(e) =>
                        handleInputChange("nameOfWork", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Tender Ref No
                    </label>
                    <input
                      type="text"
                      value={formData.tenderRefNo}
                      onChange={(e) =>
                        handleInputChange("tenderRefNo", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="date"
                      value={formData.fromDate}
                      onChange={(e) =>
                        handleInputChange("fromDate", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Client Name
                    </label>
                    <input
                      type="text"
                      value={formData.clientName}
                      onChange={(e) =>
                        handleInputChange("clientName", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Location
                    </label>
                    <select
                      value={formData.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {locations.map((loc) => (
                        <option key={loc.locationId} value={loc.locationId}>
                          {loc.locationName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Consultant Name
                    </label>
                    <input
                      type="text"
                      value={formData.consultantName}
                      onChange={(e) =>
                        handleInputChange("consultantName", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To date
                    </label>
                    <input
                      type="date"
                      value={formData.toDate}
                      onChange={(e) =>
                        handleInputChange("toDate", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Status
                    </label>
                                        <select
                      value={formData.status}
                      onChange={(e) =>
                        handleInputChange("status", e.target.value)
                      }
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="In Process">In Process</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Unsuccessful">Unsuccessful</option>
                    </select>
                  </div>
                </div>

                                <div className="md:col-span-2 col-span-1 flex justify-center items-center mt-6 gap-4">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={handleReset}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                    }`}
                  >
                    Reset
                  </button>

                  <button
                    onClick={handleViewAll}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? `${buttonClass} ${hoverClass} text-white`
                        : `${buttonClass} ${hoverClass} text-white`
                    }`}
                  >
                    <FaEye />
                    View All
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Name of The Work
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Client Name
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Consultant
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Tender Submission Date
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Status
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Prepared By
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Authorized By
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      MD Approval
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      File Manager
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Print
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {tenderData.map((item) => {
                    const isCompleted =
                      Number(item.tenderStatus) === 1 ||
                      String(item.statusText || "").toLowerCase() ===
                        "successfull";

                    const selectValue =
                      item.tenderStatus === 1
                        ? "Ongoing"
                        : item.tenderStatus === 0
                        ? "Unsuccessful"
                        : "In Process";

                    return (
                      <tr
                        key={item.tenderDetailsId}
                        className={`transition-colors duration-200 ${
                          isDark
                            ? "bg-gray-800 hover:bg-gray-750"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.rowno}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          <button
                            onClick={() => handleEditAndView(item)}
                            className={`underline text-xs ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            {item.nameOfWork}
                          </button>
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.clientName}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.consultantName || ""}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.tenderSubmissionDate}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          }`}
                        >
                          <select
                            value={selectValue}
                            disabled={isCompleted}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === "Ongoing") {
                                handleOngoing(item);
                              } else if (value === "Unsuccessful") {
                                handleUnsuccessful(item);
                              }
                            }}
                            className={`px-2 py-1 rounded text-xs border ${
                              isCompleted ? "opacity-60 cursor-not-allowed" : ""
                            } ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          >
                            <option value="In Process">In Process</option>
                            <option value="Ongoing">Ongoing</option>
                            <option value="Unsuccessful">Unsuccessful</option>
                          </select>
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.preparedBy}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.authorizedBy}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark
                              ? "border-gray-700 text-white"
                              : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.approvedBy || "Waiting"}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          }`}
                        >
                          <button
                            onClick={() => handleFileManager(item)}
                            className={`underline text-xs ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            File Manager
                          </button>
                        </td>

                        <td
                          className={`px-3 py-3 text-center ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          <button
                            onClick={() => handlePrint(item)}
                            className={`underline text-xs ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300"
                                : "text-blue-600 hover:text-blue-800"
                            }`}
                          >
                            Print
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm">Total: {pagination.totalRows}</div>

            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1 || loading}
                onClick={() => {
                  const newPage = page - 1;
                  setPage(newPage);
                  fetchTenders({ page: newPage });
                }}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="text-sm">
                Page {page} / {pagination.totalPages || 1}
              </span>

              <button
                disabled={page >= (pagination.totalPages || 1) || loading}
                onClick={() => {
                  const newPage = page + 1;
                  setPage(newPage);
                  fetchTenders({ page: newPage });
                }}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default TenderListing;